import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MapPin, Plus, Pencil, Trash2, Phone } from "lucide-react";

const EMPTY = { name: "", city: "", address: "", phone: "" };

export default function Offices() {
  const [offices, setOffices] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => setOffices((await api.get("/offices")).data), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (o) => { setEditId(o.id); setForm({ name: o.name, city: o.city || "", address: o.address || "", phone: o.phone || "" }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Office name is required"); return; }
    setSaving(true);
    try {
      if (editId) await api.put(`/offices/${editId}`, form);
      else await api.post("/offices", form);
      toast.success(editId ? "Office updated" : "Office added");
      setOpen(false); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    await api.delete(`/offices/${id}`);
    setOffices((p) => p.filter((o) => o.id !== id));
    toast.success("Office removed");
  };

  return (
    <div>
      <PageHeader eyebrow="Workspace" title="Office Locations"
        subtitle="Add and manage all your branch offices"
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-office-btn" onClick={openNew} className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold gap-2"><Plus className="w-4 h-4" /> Add Office</Button>
            </DialogTrigger>
            <DialogContent className="bg-[#05261D] border-gold/20" data-testid="office-modal">
              <DialogHeader><DialogTitle className="font-serif text-gold flex items-center gap-2"><MapPin className="w-5 h-5" /> {editId ? "Edit Office" : "New Office Location"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs text-muted-foreground">Office name</Label><Input data-testid="office-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-card/60" placeholder="e.g. Kolkata Head Office" /></div>
                <div><Label className="text-xs text-muted-foreground">City</Label><Input data-testid="office-city-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1 bg-card/60" placeholder="Kolkata" /></div>
                <div><Label className="text-xs text-muted-foreground">Address</Label><Input data-testid="office-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1 bg-card/60" placeholder="Full address" /></div>
                <div><Label className="text-xs text-muted-foreground">Phone</Label><Input data-testid="office-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 bg-card/60" placeholder="+91…" /></div>
              </div>
              <DialogFooter>
                <Button data-testid="save-office-btn" onClick={save} disabled={saving} className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold">{saving ? "Saving…" : "Save Office"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="p-4 sm:p-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {offices.length === 0 && <p className="text-sm text-muted-foreground col-span-full text-center py-12">No offices yet. Add your first branch.</p>}
        {offices.map((o) => (
          <Card key={o.id} data-testid={`office-card-${o.id}`} className="p-4 bg-card/40 border-border/60">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/40 flex items-center justify-center flex-shrink-0"><MapPin className="w-5 h-5 text-gold" /></div>
              <div className="flex-1 min-w-0">
                <p className="font-medium">{o.name}</p>
                {o.city && <p className="text-xs text-gold/80">{o.city}</p>}
                {o.address && <p className="text-xs text-muted-foreground mt-0.5">{o.address}</p>}
                {o.phone && <p className="text-[11px] text-muted-foreground font-mono mt-1 flex items-center gap-1"><Phone className="w-3 h-3" />{o.phone}</p>}
              </div>
            </div>
            <div className="flex gap-2 mt-3 justify-end">
              <Button size="icon" variant="ghost" data-testid={`office-edit-${o.id}`} onClick={() => openEdit(o)} className="h-8 w-8 text-gold hover:bg-gold/15"><Pencil className="w-3.5 h-3.5" /></Button>
              <Button size="icon" variant="ghost" data-testid={`office-delete-${o.id}`} onClick={() => remove(o.id)} className="h-8 w-8 text-destructive hover:bg-destructive/15"><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
